import { isObject } from 'radash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAcquireRecordLockMutation, useHeartbeatRecordLockMutation, useReleaseRecordLockMutation } from 'store/services/flow/agendaRecords';
import { HeartbeatReason, type AcquireRecordLockResponse } from 'types';
import { isRecord } from 'utils/typeChecks';

const HEARTBEAT_INTERVAL_MS = 30000;

type UseRecordLockParams = {
  agendaId: string;
  recordId: string;
  onLockExpired: () => void;
};

type UseRecordLockReturn = {
  isLockAcquired: boolean;
  isLockAcquiring: boolean;
  lockToken: string | null;
  lockConflict: AcquireRecordLockResponse | null;
  releaseLock: () => void;
  beginUploadingFiles: () => void;
  endUploadingFiles: () => void;
};

type SessionRef = {
  lockToken: string | null;
  reason: HeartbeatReason;
  onLockExpired: () => void;
  intervalId: ReturnType<typeof setInterval> | null;
  uploadingCount: number;
};

const isNewRecord = (recordId: string) => recordId.startsWith('new');

const isAcquireRecordLockResponse = (value: unknown): value is AcquireRecordLockResponse => {
  if (!isRecord(value)) return false;

  return (
    typeof value.success === 'boolean' &&
    (value.lockToken === null || typeof value.lockToken === 'string') &&
    (value.lockedBy === null || typeof value.lockedBy === 'string') &&
    (value.expiresAtUtc === null || typeof value.expiresAtUtc === 'string')
  );
};

export const useRecordLock = ({ agendaId, recordId, onLockExpired }: UseRecordLockParams): UseRecordLockReturn => {
  const [isLockAcquiring, setIsLockAcquiring] = useState(false);
  const [lockToken, setLockToken] = useState<string | null>(null);
  const [lockConflict, setLockConflict] = useState<AcquireRecordLockResponse | null>(null);

  const [acquireRecordLock] = useAcquireRecordLockMutation();
  const [releaseRecordLock] = useReleaseRecordLockMutation();
  const [heartbeatRecordLock] = useHeartbeatRecordLockMutation();

  const sessionRef = useRef<SessionRef>({
    lockToken: null,
    reason: HeartbeatReason.Editing,
    onLockExpired,
    intervalId: null,
    uploadingCount: 0,
  });

  const clearHeartbeatInterval = useCallback(() => {
    if (sessionRef.current.intervalId !== null) {
      clearInterval(sessionRef.current.intervalId);
      sessionRef.current.intervalId = null;
    }
  }, []);

  const clearLockState = useCallback(() => {
    sessionRef.current.lockToken = null;
    sessionRef.current.uploadingCount = 0;
    sessionRef.current.reason = HeartbeatReason.Editing;
    setLockToken(null);
  }, []);

  const releaseLock = useCallback(() => {
    const { lockToken: token } = sessionRef.current;
    if (!token || isNewRecord(recordId)) return;

    releaseRecordLock({ agendaId, recordId, lockToken: token });
    clearLockState();
  }, [agendaId, clearLockState, recordId, releaseRecordLock]);

  const startHeartbeat = useCallback(() => {
    clearHeartbeatInterval();

    sessionRef.current.intervalId = setInterval(async () => {
      const { lockToken: token, reason, onLockExpired: notifyExpired } = sessionRef.current;
      if (!token) return;

      try {
        const result = await heartbeatRecordLock({
          agendaId,
          recordId,
          lockToken: token,
          reason,
        }).unwrap();

        if (!result.isValid) {
          clearHeartbeatInterval();
          clearLockState();
          notifyExpired();
        }
      } catch {
        // heartbeat failures are handled on the next tick or via release
      }
    }, HEARTBEAT_INTERVAL_MS);
  }, [agendaId, clearHeartbeatInterval, clearLockState, heartbeatRecordLock, recordId]);

  const beginUploadingFiles = useCallback(() => {
    sessionRef.current.uploadingCount += 1;
    sessionRef.current.reason = HeartbeatReason.UploadingFiles;
  }, []);

  const endUploadingFiles = useCallback(() => {
    sessionRef.current.uploadingCount = Math.max(0, sessionRef.current.uploadingCount - 1);
    if (sessionRef.current.uploadingCount === 0) {
      sessionRef.current.reason = HeartbeatReason.Editing;
    }
  }, []);

  useEffect(() => {
    sessionRef.current.onLockExpired = onLockExpired;
  }, [onLockExpired]);

  useEffect(() => {
    if (isNewRecord(recordId)) return;

    let cancelled = false;
    const requestAgendaId = agendaId;
    const requestRecordId = recordId;

    const acquireLock = async () => {
      setIsLockAcquiring(true);
      setLockConflict(null);

      try {
        const response = await acquireRecordLock({
          agendaId: requestAgendaId,
          recordId: requestRecordId,
          suppressErrorToast: true,
        }).unwrap();

        if (cancelled) {
          if (response.success && response.lockToken) {
            releaseRecordLock({
              agendaId: requestAgendaId,
              recordId: requestRecordId,
              lockToken: response.lockToken,
            });
          }
          return;
        }

        if (response.success && response.lockToken) {
          sessionRef.current.lockToken = response.lockToken;
          setLockToken(response.lockToken);
          setLockConflict(null);
          startHeartbeat();
        } else {
          setLockConflict(response);
        }
      } catch (error) {
        if (cancelled) return;

        const conflictDataFromError = isObject(error) && 'data' in error && isAcquireRecordLockResponse(error.data) ? error.data : null;

        if (conflictDataFromError?.success === false) {
          setLockConflict(conflictDataFromError);
          return;
        }

        setLockConflict(null);
      } finally {
        if (!cancelled) {
          setIsLockAcquiring(false);
        }
      }
    };

    acquireLock();

    const handleBeforeUnload = () => {
      releaseLock();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      cancelled = true;
      clearHeartbeatInterval();
      window.removeEventListener('beforeunload', handleBeforeUnload);
      releaseLock();
    };
  }, [acquireRecordLock, agendaId, clearHeartbeatInterval, recordId, releaseLock, releaseRecordLock, startHeartbeat]);

  return {
    isLockAcquired: Boolean(lockToken),
    isLockAcquiring,
    lockToken,
    lockConflict,
    releaseLock,
    beginUploadingFiles,
    endUploadingFiles,
  };
};
